import { ReactNode } from 'react';
import { useProtectedRoute } from '../../hooks/useProtectedRoute';
import { useAuth } from '../../contexts/AuthContext';
import BottomNav from './BottomNav';
import { LayoutDashboard, PlusCircle, Sprout, Tag, Users, Settings, LogOut } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NotificationsProvider } from '../../contexts/NotificationsContext';

interface AppLayoutProps {
    children: ReactNode;
    title?: string;
    hideUser?: boolean;
}

export default function AppLayout({ children, hideUser = false }: AppLayoutProps) {
    return (
        <NotificationsProvider>
            <AppLayoutContent hideUser={hideUser}>{children}</AppLayoutContent>
        </NotificationsProvider>
    );
}

function AppLayoutContent({ children, hideUser = false }: AppLayoutProps) {
    const { loading } = useProtectedRoute();
    const { user, signOut } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-dark-bg">
                <div className="flex flex-col items-center">
                    <Sprout className="text-accent-purple animate-pulse mb-4" size={48} />
                    <p className="text-dark-muted animate-pulse">Initializing Dashboard...</p>
                </div>
            </div>
        );
    }

    const navItems = [
        { to: '/', icon: LayoutDashboard, label: 'Home' },
        { to: '/new-session', icon: PlusCircle, label: 'New' },
        { to: '/strains', icon: Sprout, label: 'Strains' },
        { to: '/brands', icon: Tag, label: 'Brands' },
        { to: '/compare', icon: Users, label: 'Duo' },
    ];

    return (
        <div className="min-h-screen bg-dark-bg text-dark-text pb-24">
            {/* Header */}
            <header className="sticky top-0 bg-dark-bg/60 backdrop-blur-xl border-b border-dark-border/40 z-40 safe-top">
                <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3 group cursor-pointer" onClick={() => navigate('/')}>
                        <div className="relative">
                            <div className="absolute inset-0 bg-accent-purple/20 blur-lg rounded-full animate-pulse-slow group-hover:bg-accent-purple/40" />
                            <div className="relative bg-dark-bg/80 p-2 rounded-xl border border-white/5 shadow-inner">
                                <Sprout className="text-accent-purple" size={18} />
                            </div>
                        </div>
                        <h1 className="font-black text-lg tracking-tight bg-gradient-to-r from-white to-dark-muted bg-clip-text text-transparent italic group-hover:to-white transition-all">
                            Joint<span className="text-accent-purple">Session</span>
                        </h1>
                    </div>

                    <div className="flex items-center bg-dark-surface/30 p-1 rounded-full border border-white/5 backdrop-blur-sm shadow-inner group">
                        <button
                            onClick={() => navigate('/settings')}
                            className={`flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full transition-all duration-300 ${location.pathname === '/settings'
                                ? 'bg-accent-purple text-white shadow-lg shadow-accent-purple/20'
                                : 'text-dark-muted hover:text-white'
                                }`}
                        >
                            {!hideUser && user && (
                                <div className="flex items-center gap-2">
                                    <div className="w-5 h-5 rounded-full bg-accent-purple/20 border border-accent-purple/30 flex items-center justify-center overflow-hidden">
                                        <span className="text-[10px] font-black uppercase text-accent-purple leading-none">
                                            {user.email?.[0]}
                                        </span>
                                    </div>
                                    <span className="text-[10px] font-black uppercase tracking-widest hidden xs:block">Settings</span>
                                </div>
                            )}
                            <Settings size={14} className={location.pathname === '/settings' ? '' : 'text-dark-muted/60'} />
                        </button>

                        <div className="w-px h-4 bg-dark-border/40 mx-1" />

                        <button
                            onClick={signOut}
                            className="p-2 text-dark-muted hover:text-accent-red transition-all group-hover:scale-105 active:scale-95"
                            title="Sign Out"
                        >
                            <LogOut size={14} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-2xl mx-auto px-4 py-6">
                {children}
            </main>

            {/* Bottom Navigation */}
            <BottomNav navItems={navItems} />
        </div>
    );
}
